import React, { useMemo } from "react";
import { Tag } from "antd";
import type { ColumnsType } from "antd/es/table";

import DataTableBase from "../../shared/dashboards/DataTable/DataTable";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { convertToUserTimezone } from "../../../utils/dateService";
import type {
  DuplicateIdentityClassification,
  DuplicateIdentityDupType,
  DuplicateIdentityGroupSummary,
  DuplicateIdentityStatus,
} from "../../../queries/duplicate-identity/types";
import { useDuplicateIdentityReview } from "./DuplicateIdentityReviewContext";
import styles from "./stylesDuplicateIdentityReview.module.css";

const DataTable = DataTableBase as React.ComponentType<{
  rowKey?: string;
  columns?: ColumnsType<DuplicateIdentityGroupSummary>;
  dataSource?: DuplicateIdentityGroupSummary[];
  pagination?: boolean | object;
  scrollPreset?: string;
  loading?: boolean;
  onRow?: (record: DuplicateIdentityGroupSummary) => {
    onClick?: () => void;
  };
}>;

const DUP_TYPE_LABELS: Record<DuplicateIdentityDupType, string> = {
  phone_raw: AppStrings.KYC_DUPLICATE_IDENTITY_DUP_TYPE_PHONE_RAW,
  phone_normalized: AppStrings.KYC_DUPLICATE_IDENTITY_DUP_TYPE_PHONE_NORMALIZED,
  email_case_folded:
    AppStrings.KYC_DUPLICATE_IDENTITY_DUP_TYPE_EMAIL_CASE_FOLDED,
  f9_malformed: AppStrings.KYC_DUPLICATE_IDENTITY_DUP_TYPE_F9_MALFORMED,
};

const CLASSIFICATION_LABELS: Record<DuplicateIdentityClassification, string> = {
  merge_candidate:
    AppStrings.KYC_DUPLICATE_IDENTITY_CLASSIFICATION_MERGE_CANDIDATE,
  support_contact:
    AppStrings.KYC_DUPLICATE_IDENTITY_CLASSIFICATION_SUPPORT_CONTACT,
  f9_manual: AppStrings.KYC_DUPLICATE_IDENTITY_CLASSIFICATION_F9_MANUAL,
};

const STATUS_LABELS: Record<DuplicateIdentityStatus, string> = {
  pending_review: AppStrings.KYC_DUPLICATE_IDENTITY_STATUS_PENDING_REVIEW,
  dry_run_planned: AppStrings.KYC_DUPLICATE_IDENTITY_STATUS_DRY_RUN_PLANNED,
  merged: AppStrings.KYC_DUPLICATE_IDENTITY_STATUS_MERGED,
  support_contacted:
    AppStrings.KYC_DUPLICATE_IDENTITY_STATUS_SUPPORT_CONTACTED,
  escalated: AppStrings.KYC_DUPLICATE_IDENTITY_STATUS_ESCALATED,
  ignored: AppStrings.KYC_DUPLICATE_IDENTITY_STATUS_IGNORED,
};

export function dupTypeLabel(value: DuplicateIdentityDupType): string {
  return DUP_TYPE_LABELS[value] ?? value;
}

export function classificationLabel(
  value: DuplicateIdentityClassification,
): string {
  return CLASSIFICATION_LABELS[value] ?? value;
}

export function statusLabel(value: DuplicateIdentityStatus): string {
  return STATUS_LABELS[value] ?? value;
}

const DuplicateIdentityGroupsTable: React.FC = () => {
  const {
    groups,
    isLoading,
    isFetching,
    isError,
    currentPage,
    hasNextPage,
    goToNextPage,
    goToPreviousPage,
    pageSize,
    openDetail,
  } = useDuplicateIdentityReview();

  const dataSource = groups?.items ?? [];
  // The API only supports "next page after this cursor" — there's no way to seek
  // to an arbitrary page number or learn a real total (see
  // `DuplicateIdentityGroupPage` on the API side), so `total` here isn't a real
  // count. It's only shifted just past/at the current page boundary so antd's
  // built-in prev/next arrows enable or disable in agreement with what the
  // backend told us via `hasNextPage`.
  const total = hasNextPage
    ? currentPage * pageSize + 1
    : currentPage * pageSize;

  const columns: ColumnsType<DuplicateIdentityGroupSummary> = useMemo(
    () => [
      {
        title: AppStrings.KYC_DUPLICATE_IDENTITY_TENANT,
        dataIndex: "tenant",
        key: "tenant",
        width: 140,
      },
      {
        title: AppStrings.KYC_DUPLICATE_IDENTITY_DUP_TYPE,
        dataIndex: "dup_type",
        key: "dup_type",
        width: 180,
        render: (value: DuplicateIdentityDupType) => (
          <Tag>{dupTypeLabel(value)}</Tag>
        ),
      },
      {
        title: AppStrings.KYC_DUPLICATE_IDENTITY_CLASSIFICATION,
        dataIndex: "classification",
        key: "classification",
        width: 160,
        render: (value: DuplicateIdentityClassification) => (
          <Tag color="blue">{classificationLabel(value)}</Tag>
        ),
      },
      {
        title: AppStrings.KYC_DUPLICATE_IDENTITY_GROUP_SIZE,
        dataIndex: "user_ids",
        key: "group_size",
        width: 120,
        render: (userIds: string[]) => userIds?.length ?? 0,
      },
      {
        title: AppStrings.KYC_DUPLICATE_IDENTITY_DETECTED_AT,
        dataIndex: "detected_at",
        key: "detected_at",
        width: 200,
        render: (value: string) =>
          value ? convertToUserTimezone(value) : "—",
      },
      {
        title: AppStrings.KYC_DUPLICATE_IDENTITY_STATUS,
        dataIndex: "status",
        key: "status",
        width: 160,
        render: (value: DuplicateIdentityStatus) => (
          <Tag>{statusLabel(value)}</Tag>
        ),
      },
    ],
    [],
  );

  if (isLoading && !dataSource.length) {
    return <RebetLoader height="32px" width="32px" />;
  }

  if (isError) {
    return (
      <div
        className={`${styles.stateMessage} ${styles.stateMessageError}`}
        data-testid="duplicate-identity-groups-error"
      >
        {AppStrings.KYC_DUPLICATE_IDENTITY_LOAD_ERROR}
      </div>
    );
  }

  if (!isLoading && !dataSource.length) {
    return (
      <div
        className={styles.stateMessage}
        data-testid="duplicate-identity-groups-empty"
      >
        {AppStrings.KYC_DUPLICATE_IDENTITY_EMPTY}
      </div>
    );
  }

  return (
    <DataTable
      rowKey="group_id"
      columns={columns}
      dataSource={dataSource}
      loading={isLoading}
      scrollPreset="default"
      pagination={{
        current: currentPage,
        pageSize,
        total,
        showSizeChanger: false,
        // Belt-and-suspenders with the context's isFetching guard on
        // goToNextPage/goToPreviousPage: disable the arrows visually too, so a
        // double-click can't queue a second navigation before the in-flight
        // page's cursor has landed. `isFetching` (not `isLoading`) is required
        // here — the query keeps previous data while paginating
        // (`placeholderData: keepPreviousData`), so `isLoading` only reflects
        // the very first fetch and would never catch a page-to-page race.
        disabled: isFetching,
        // Cursor-based backend: no random page access. Only the prev/next arrows
        // are rendered (page-number and jump-±5 buttons are suppressed below), and
        // each arrow click resolves to the one cursor we already know about.
        itemRender: (
          _page: number,
          type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
          originalElement: React.ReactNode,
        ) =>
          type === "page" || type === "jump-prev" || type === "jump-next"
            ? null
            : originalElement,
        onChange: (page: number) => {
          if (page > currentPage) {
            goToNextPage();
          } else if (page < currentPage) {
            goToPreviousPage();
          }
        },
      }}
      onRow={(record) => ({
        onClick: () => openDetail(record.group_id),
      })}
    />
  );
};

export default DuplicateIdentityGroupsTable;
